import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Currency from 'components/currency';
import Incrementor from 'components/incrementor';
import { messages } from 'components/incrementor/messages';
import VisuallyHidden from 'components/visually-hidden';
import { priceForCartEntry } from 'utils/menu/price';

import { CartEntryTitle } from './styled/cart-entry-title';
import { CartPreviewEntryContainer } from './styled/cart-preview-entry-container';
import { CartPreviewItem } from './styled/cart-preview-item';
import { ItemPrice } from './styled/item-price';
import { RemoveButton } from './styled/remove-button';
import { ICartPreviewEntry } from './types';

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  margin: 0 0 -0.5rem 0;
`;

const PeopleLabel = styled.span`
  color: ${Styles.color.grey.one};
  margin-right: 1rem;
`;

const CartPreviewEntry: React.FC<ICartPreviewEntry> = ({
  item,
  clearSelectedOffer,
  selectedOfferCartEntry: offerEntry,
  selectedOfferPrice,
  removeFromCart,
  updateQuantity
}) => {
  const { formatMessage } = useIntl();
  const isOffer = offerEntry && offerEntry.sanityId === item.sanityId;

  const handleRemoveItem = useCallback(() => {
    if (isOffer) {
      return clearSelectedOffer();
    }

    removeFromCart(item.cartId);
  }, [clearSelectedOffer, isOffer, item.cartId, removeFromCart]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      updateQuantity(item.cartId, value);
    },
    [item.cartId, updateQuantity]
  );

  const itemPrice = isOffer ? selectedOfferPrice : priceForCartEntry(item);

  return (
    <CartPreviewEntryContainer>
      <CartPreviewItem>
        <CartEntryTitle data-testid="cart-preview-name">{item.name}</CartEntryTitle>
        <ItemPrice>
          <Currency amount={itemPrice} />
        </ItemPrice>
      </CartPreviewItem>
      <ActionsContainer>
        <RemoveButton onClick={handleRemoveItem} aria-label={`Remove ${item.name}`}>
          {formatMessage({
            id: 'remove'
          })}
        </RemoveButton>
        <Incrementor
          name={item.name}
          border
          aria-label={`Quantity ${item.quantity} of ${item.name}`}
          data-testId="cart-preview-quantity"
          value={item.quantity}
          onChange={handleQuantityChange}
          min={1}
          // This number 9 doesn’t come from Sanity, this is cross brand based on POS limitations on double digit numbers.
          // https://rbictg.atlassian.net/secure/RapidBoard.jspa?rapidView=1&modal=detail&selectedIssue=CBA-1032&quickFilter=12&assignee=5d854eb836a52a0da7914374
          max={9}
        >
          {item.labelAsPerPerson && (
            <PeopleLabel>
              {formatMessage({
                id: 'people'
              })}
              :
            </PeopleLabel>
          )}
        </Incrementor>
      </ActionsContainer>

      <VisuallyHidden role="alert">
        {formatMessage(messages.quantityUpdatedAlert, {
          quantity: item.quantity,
          itemName: item.name
        })}
      </VisuallyHidden>
    </CartPreviewEntryContainer>
  );
};

export default CartPreviewEntry;
